/**
===================================================
Module: frontend.src.components.AdminSettings.tsx
Description: Admin panel for system settings
Owner: Kawada Technosystem Co Ltd
All Rights Reserved
This script is proprietary and confidential.
Unauthorized copying, distribution, or modification of this
file, via any medium, is strictly prohibited.
===================================================
*/

import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { apiService } from "../services/api";
import { SystemSettings } from "../types";
import TopBar from "./TopBar";
import NotificationSystem, { Notification } from "./NotificationSystem";
import "../styles/AdminUsers.css"; // Reuse admin styles

const AdminSettings: React.FC = () => {
    const { t } = useTranslation('admin');
    const [settings, setSettings] = useState<SystemSettings>({
        document_retention_days: 30,
        account_expiry_days: 30
    });
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [notifications, setNotifications] = useState<Notification[]>([]);

    const addNotification = (notification: Omit<Notification, 'id'>) => {
        const id = Date.now().toString();
        setNotifications(prev => [...prev, { ...notification, id }]);
        return id;
    };

    const removeNotification = (id: string) => {
        setNotifications(prev => prev.filter(n => n.id !== id));
    };

    useEffect(() => {
        loadSettings();
    }, []);

    const loadSettings = async () => {
        try {
            setIsLoading(true);
            const response = await apiService.getSettings();
            setSettings(response);
        } catch (err) {
            addNotification({ type: 'error', message: t('notifications.loadError') });
        } finally {
            setIsLoading(false);
        }
    };

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();

        // Validation: All values must be > 0
        const valuesToValidate = [
            { val: settings.document_retention_days, label: t('settings.retention') },
            { val: settings.account_expiry_days, label: t('settings.expiry') },
            { val: settings.max_file_size_mb, label: t('settings.maxFileSize') },
            { val: settings.max_file_pages, label: t('settings.maxFilePages') },
            { val: settings.total_page_limit, label: t('settings.defaultTotalPages') }
        ];

        for (const item of valuesToValidate) {
            if (item.val !== undefined && item.val <= 0) {
                addNotification({ type: 'error', message: `${item.label}: ${t('validationError')}` });
                return;
            }
        }

        try {
            setIsSaving(true);
            await apiService.updateSettings(settings);
            addNotification({ type: 'success', message: t('settings.saveSuccess'), duration: 3000 });
        } catch (err) {
            addNotification({ type: 'error', message: "Failed to save settings" });
        } finally {
            setIsSaving(false);
        }
    };

    const handleNumberKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === '-' || e.key === 'e' || e.key === 'E' || e.key === '.') {
            e.preventDefault();
        }
    };

    return (
        <div className="admin-container">
            <div className="admin-content">
                <TopBar />

                <div className="admin-header-section">
                    <div className="breadcrumb">
                        <span className="breadcrumb-link" onClick={() => window.location.href = '/projects'}>
                            {t('common:home', { defaultValue: 'Home' })}
                        </span>
                        <span className="breadcrumb-separator">/</span>
                        <span className="breadcrumb-link" onClick={() => window.location.href = '/admin'}>
                            {t('title')}
                        </span>
                        <span className="breadcrumb-separator">/</span>
                        <span>{t('settingsTitle')}</span>
                    </div>

                    <div className="admin-title-row">
                        <h1>{t('settingsTitle')}</h1>
                        <button
                            className="btn-secondary btn-with-icon"
                            onClick={() => window.location.href = '/admin/users'}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                                <circle cx="9" cy="7" r="4"></circle>
                                <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                            </svg>
                            {t('title', { defaultValue: 'Manage Users' })}
                        </button>
                    </div>
                </div>

                <div className="admin-main">
                    <div className="settings-form-container">
                        <form onSubmit={handleSave} className="admin-form">
                            <div className="form-section">
                                <h3>{t('settings.retentionTitle')}</h3>
                                <p className="section-desc">
                                    {t('settings.retentionDesc')}
                                </p>
                                <div className="form-group">
                                    <label>{t('settings.retention')}</label>
                                    <input
                                        type="number"
                                        min="1"
                                        value={settings.document_retention_days}
                                        onKeyDown={handleNumberKeyDown}
                                        onChange={(e) => setSettings({ ...settings, document_retention_days: parseInt(e.target.value) || 0 })}
                                        required
                                    />
                                </div>
                            </div>

                            <div className="form-section">
                                <h3>{t('settings.expiryTitle')}</h3>
                                <p className="section-desc">
                                    {t('settings.expiryDesc')}
                                </p>
                                <div className="form-group">
                                    <label>{t('settings.expiry')}</label>
                                    <input
                                        type="number"
                                        min="1"
                                        value={settings.account_expiry_days}
                                        onKeyDown={handleNumberKeyDown}
                                        onChange={(e) => setSettings({ ...settings, account_expiry_days: parseInt(e.target.value) || 0 })}
                                        required
                                    />
                                </div>
                            </div>

                            <div className="form-section">
                                <h3>{t('settings.helpManualTitle', { defaultValue: 'Help Manual' })}</h3>
                                <p className="section-desc">
                                    {t('settings.helpManualDesc', { defaultValue: 'URL for the help manual link displayed in the top navigation bar.' })}
                                </p>
                                <div className="form-group">
                                    <label>{t('settings.helpManualUrl', { defaultValue: 'Manual URL' })}</label>
                                    <input
                                        type="url"
                                        value={settings.help_manual_url || ""}
                                        onChange={(e) => setSettings({ ...settings, help_manual_url: e.target.value })}
                                        placeholder="https://example.com/manual.pdf"
                                    />
                                    <p className="help-text">{t('settings.helpManualUrlDesc', { defaultValue: 'Leave empty to hide the help button. Enter a full URL (e.g. https://...)' })}</p>
                                </div>
                            </div>

                            {/* Advanced Settings Section */}
                            <div className="form-section">
                                <details>
                                    <summary className="advanced-summary">
                                        {t('settings.advancedTitle')}
                                    </summary>

                                    <div className="advanced-content">
                                        <div className="form-group">
                                            <label>{t('settings.maxFileSize')}</label>
                                            <input
                                                type="number"
                                                min="1"
                                                value={settings.max_file_size_mb || ""}
                                                onKeyDown={handleNumberKeyDown}
                                                onChange={(e) => setSettings({ ...settings, max_file_size_mb: parseInt(e.target.value) || 0 })}
                                            />
                                            <p className="help-text">{t('settings.maxFileSizeDesc')}</p>
                                        </div>

                                        <div className="form-group">
                                            <label>{t('settings.maxFilePages')}</label>
                                            <input
                                                type="number"
                                                min="1"
                                                value={settings.max_file_pages || ""}
                                                onKeyDown={handleNumberKeyDown}
                                                onChange={(e) => setSettings({ ...settings, max_file_pages: parseInt(e.target.value) || 0 })}
                                            />
                                            <p className="help-text">{t('settings.maxFilePagesDesc')}</p>
                                        </div>

                                        <div className="form-group">
                                            <label>{t('settings.defaultTotalPages')}</label>
                                            <input
                                                type="number"
                                                min="1"
                                                value={settings.total_page_limit || ""}
                                                onKeyDown={handleNumberKeyDown}
                                                onChange={(e) => setSettings({ ...settings, total_page_limit: parseInt(e.target.value) || 0 })}
                                            />
                                            <p className="help-text">{t('settings.defaultTotalPagesDesc')}</p>
                                        </div>
                                    </div>
                                </details>
                            </div>

                            <div className="modal-actions settings-actions">
                                <button type="submit" className="btn-primary" disabled={isLoading || isSaving}>
                                    {isSaving ? t('editUser.saving') : t('editUser.save')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
            <NotificationSystem notifications={notifications} onRemove={removeNotification} />
        </div>
    );
};

export default AdminSettings;
